import { initializeApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
// import { getAuth, signInWithCustomToken } from 'firebase/auth'
import { getFirestore, enableMultiTabIndexedDbPersistence, collection, doc, getDoc, getDocs, setDoc, query, where, limit, writeBatch, deleteDoc, addDoc, onSnapshot } from 'firebase/firestore'
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { getAnalytics } from 'firebase/analytics'
import { getFunctions } from 'firebase/functions'
import Vue from 'vue'
import { User, Isbn, Page, Sheet, Project, Template, CoverTemplate, getFormatKey, COVER_TYPE_TEMPLATE } from '../model'
import domtoimage from 'dom-to-image'

// window.authWithToken = (token) => signInWithCustomToken(getAuth(), token)

const firebaseConfig = {
  apiKey: 'AIzaSyBbLvXDC8fWlZ7Kib-03xjuEiNtJMZA--s',
  authDomain: 'bookoapp.firebaseapp.com',
  projectId: 'bookoapp',
  storageBucket: 'bookoapp.appspot.com',
  messagingSenderId: '26539849579',
  appId: '1:26539849579:web:cf9ec7e2d0bb5bfc3f3e2e',
  measurementId: 'G-9ZBMN8Q279'
}

const Firebase = {
  install (Vue, options) {
    const app = initializeApp(firebaseConfig)
    const db = getFirestore(app)
    // Мультитабный режим: однотабный enableIndexedDbPersistence берёт эксклюзивный
    // лок на IndexedDB, и любая вторая вкладка приложения падает с failed-precondition.
    enableMultiTabIndexedDbPersistence(db).catch((error) => {
      // Кэш недоступен (например, приватный режим) — работаем без офлайн-кэша.
      console.warn('Firestore persistence disabled:', error.code)
    })
    getAuth(app).useDeviceLanguage()
    getAnalytics(app)

    Vue.prototype.$fb = app
    Vue.prototype.$fn = getFunctions(app, 'asia-northeast1')
    Vue.prototype.$db = {
      async getProjects (ref, loadSheets = false) {
        const snapshot = await getDocs(ref)
        const projects = await Promise.all(snapshot.docs.map((doc) => this.getProject(doc.ref, loadSheets)))
        return projects.filter((p) => p !== null)
      },

      async getProject (ref, loadSheets = false) {
        let sheets = []

        if (loadSheets) {
          const sheetsRef = collection(ref, 'sheets')
          sheets = await this.getSheets(sheetsRef)
        }

        const snapshot = await getDoc(ref)
        const data = snapshot.data()

        if (!data) return null

        if (data.template) {
          data.template = await this.getTemplate(data.template, loadSheets)
        } else {
          data.template = null
        }

        // Обложечные данные нужны только в дизайнере/превью: в списке проектов это лишние чтения.
        if (loadSheets) {
          if (data.cover?.type === COVER_TYPE_TEMPLATE) {
            data.coverPages = await this.getCoverPages(data.cover)
          }

          if (data.template) {
            data.coverTemplate = await this.getCoverTemplate(data.template.printInfo)
          }
        }

        return new Project(ref, data, sheets)
      },

      /*
        Шаблон обложки для формата книги: top-level коллекция covers/{coverId},
        подбор по обрезному формату (formatKey) и стороне корешка. Один документ
        обслуживает все книжные шаблоны этого формата — обложки не дублируются.
      */
      async getCoverTemplate (printInfo) {
        if (!printInfo?.format) return null

        const q = query(
          collection(db, 'covers'),
          where('formatKey', '==', getFormatKey(printInfo.format)),
          where('spinSide', '==', printInfo.spinSide || 'left'),
          where('available', '==', true),
          limit(1)
        )
        const snapshot = await getDocs(q)

        if (snapshot.empty) return null

        const coverDocSnap = snapshot.docs[0]
        const pagesSnapshot = await getDocs(collection(coverDocSnap.ref, 'pages'))
        const pages = pagesSnapshot.docs.map((pageDoc) => new Page(pageDoc.ref, pageDoc.data()))

        return new CoverTemplate(coverDocSnap.ref, coverDocSnap.data(), pages)
      },

      /* Страницы шаблонной обложки проекта: общая задняя + по документу на каждый вариант. */
      async getCoverPages (cover) {
        const variantEntries = Object.entries(cover.variants || {}).filter(([, pageRef]) => Boolean(pageRef))

        const [back, ...variantPages] = await Promise.all([
          cover.backPage ? this.getPage(cover.backPage) : null,
          ...variantEntries.map(([, pageRef]) => this.getPage(pageRef))
        ])

        const variants = {}
        variantEntries.forEach(([coverId], index) => {
          variants[coverId] = variantPages[index]
        })

        return { back, variants }
      },

      async deleteProject (project) {
        await deleteDoc(project._ref)
      },

      async getTemplate (ref, loadSheets = false) {
        let sheets = []

        if (loadSheets) {
          const sheetsRef = collection(ref, 'sheets')
          sheets = await this.getSheets(sheetsRef)
        }

        const snapshot = await getDoc(ref)
        const data = snapshot.data()

        return new Template(ref, data, sheets)
      },

      async getTemplates (loadSheets = false) {
        const ref = collection(db, 'templates')
        const q = query(ref, where('available', '==', true))
        const snapshot = await getDocs(q)
        return Promise.all(snapshot.docs.map((doc) => this.getTemplate(doc.ref, loadSheets)))
      },

      async getSheets (ref) {
        const snapshot = await getDocs(ref)
        return Promise.all(snapshot.docs.map((doc) => this.getSheet(doc.ref)))
      },

      async getSheet (ref) {
        const snapshot = await getDoc(ref)
        const data = snapshot.data()

        data.pages = await Promise.all(data.pages.map((pageRef) => this.getPage(pageRef)))

        return new Sheet(ref, data)
      },

      async getPage (ref) {
        const snapshot = await getDoc(ref)
        const data = snapshot.data()

        // Страницы самих шаблонов (книжных и обложечных) собственного шаблона не имеют.
        const isTemplateSource = ref._path.segments.includes('templates') || ref._path.segments.includes('covers')

        if (!isTemplateSource) {
          data.template = await this.getPageTemplate(ref, data)
        }

        return new Page(ref, data)
      },

      async getPageTemplate (pageRef, pageData) {
        if (pageData?.template) {
          return await this.getPage(pageData.template)
        } else {
          const projectRef = pageRef.parent.parent
          const projectSnapshot = await getDoc(projectRef)
          const projectData = projectSnapshot.data()
          const templateRef = projectData.template
          const templatePages = collection(templateRef, 'pages')
          const pageTemplatesSnapshot = await getDocs(query(templatePages, where('name', '==', pageData.name), where('side', '==', pageData.side)))

          if (pageTemplatesSnapshot.size > 0) {
            return await this.getPage(pageTemplatesSnapshot.docs[0].ref)
          }
        }
      },

      async initializeProjectStructure (projectRef, templateRef, defaultSheets) {
        if (!defaultSheets || defaultSheets.length === 0) return

        const db = getFirestore()
        const batch = writeBatch(db)

        const pageTemplatesRef = collection(templateRef, 'pages')
        const pageTemplatesSnap = await getDocs(pageTemplatesRef)
        const pageTemplatesMap = {}
        pageTemplatesSnap.forEach((doc) => {
          pageTemplatesMap[doc.id] = doc.data()
        })

        const projectPagesRef = collection(projectRef, 'pages')
        const projectSheetsRef = collection(projectRef, 'sheets')

        const genRef = (collectionRef) => {
          let r
          do {
            r = doc(collectionRef)
          } while (r.id[0] >= '0' && r.id[0] <= '9')
          return r
        }

        defaultSheets.forEach((sheetConfig, index) => {
          let order
          if (index === 0) {
            order = 0
          } else if (index === defaultSheets.length - 1) {
            order = 9999
          } else {
            order = index
          }

          const sheetRef = genRef(projectSheetsRef)
          const pageRefs = []

          sheetConfig.pages.forEach((pConfig) => {
            const pageTemplateData = pageTemplatesMap[pConfig.templatePageId]
            if (!pageTemplateData) return

            const pageRef = genRef(projectPagesRef)
            pageRefs.push(pageRef)

            const pageTemplateDocRef = doc(db, `templates/${templateRef.id}/pages/${pConfig.templatePageId}`)

            const pageData = {
              name: pageTemplateData.name,
              side: pConfig.side,
              format: pageTemplateData.format,
              layout: pageTemplateData.layout || '',
              style: pageTemplateData.style || '',
              script: pageTemplateData.script || '',
              colorPrint: pageTemplateData.colorPrint ?? true,
              template: pageTemplateDocRef,
              fields: pageTemplateData.fields || pageTemplateData.data || {},
              images: pageTemplateData.images || {},
              cssVariables: pageTemplateData.cssVariables || []
            }

            batch.set(pageRef, pageData)
          })

          if (pageRefs.length === 0) return

          const firstPageConfig = sheetConfig.pages[0]
          const firstPageTemplateData = firstPageConfig ? pageTemplatesMap[firstPageConfig.templatePageId] : null
          const format = firstPageTemplateData ? firstPageTemplateData.format : { width: 128, height: 188, units: 'mm' }

          const sheetData = {
            order,
            format,
            pages: pageRefs
          }
          batch.set(sheetRef, sheetData)
        })

        await batch.commit()
      },

      async setProject (project) {
        const data = project.toObject()
        await setDoc(project._ref, data, { merge: true })
      },

      /*
        Применяет вариант шаблонной обложки к проекту.

        Документы страниц создаются один раз на вариант и при переключении не удаляются,
        поэтому правки пользователя переживают переключение cover-a ↔ cover-b.
      */
      async setProjectCoverTemplate (project, variantId) {
        const coverTemplate = project.coverTemplate
        const variant = coverTemplate?.getVariant(variantId)
        if (!variant) throw new Error(`Cover variant "${variantId}" is not available for this book format`)

        const batch = writeBatch(db)
        const cover = Object.assign({}, project.cover, {
          type: COVER_TYPE_TEMPLATE,
          coverId: coverTemplate._id,
          templateCoverId: variantId
        })
        const coverPages = {
          back: project.coverPages?.back || null,
          variants: Object.assign({}, project.coverPages?.variants)
        }
        const variantRefs = Object.assign({}, cover.variants)

        if (!coverPages.back) {
          const backTemplate = coverTemplate.getPage(variant.backPageId)
          if (!backTemplate) throw new Error(`Back cover page "${variant.backPageId}" is missing`)

          coverPages.back = project.createPage(backTemplate)
          batch.set(coverPages.back._ref, coverPages.back.toObject())
        }

        if (!coverPages.variants[variantId]) {
          const frontTemplate = coverTemplate.getPage(variant.frontPageId)
          if (!frontTemplate) throw new Error(`Front cover page "${variant.frontPageId}" is missing`)

          coverPages.variants[variantId] = project.createPage(frontTemplate)
          batch.set(coverPages.variants[variantId]._ref, coverPages.variants[variantId].toObject())
        }

        cover.backPage = coverPages.back._ref
        Object.entries(coverPages.variants).forEach(([id, page]) => {
          variantRefs[id] = page._ref
        })
        cover.variants = variantRefs

        batch.set(project._ref, { cover }, { merge: true })
        await batch.commit()

        project.cover = cover
        project.coverPages = coverPages

        return project
      },

      async setProjectWithPages (project, pages = []) {
        const batch = writeBatch(db)
        const projectData = project.toObject()
        batch.set(project._ref, projectData, { merge: true })

        if (pages && pages.length > 0) {
          pages.forEach((page) => {
            const pageData = page.toObject()
            batch.set(page._ref, pageData, { merge: true })
          })
        }

        await batch.commit()
      },

      async setSheet (sheet) {
        const data = sheet.toObject()
        await Promise.all([
          ...sheet.pages.map((page) => this.setPage(page)),
          setDoc(sheet._ref, data)
        ])
      },

      async setPage (page) {
        const data = page.toObject()
        await setDoc(page._ref, data)
      },

      async getIsbn () {
        const ref = collection(db, 'isbn')
        const snapshot = await getDocs(ref)
        return Promise.all(snapshot.docs.map((doc) => {
          const data = doc.data()
          return new Isbn(doc.ref, data)
        }))
      },

      async getIsbnForProject (projectRef) {
        const ref = collection(db, 'isbn')
        const q = query(ref, where('project', '==', projectRef), where('ownerId', '==', getAuth().currentUser.uid), where('status', 'in', ['pending', 'unavailable']))
        const snapshot = await getDocs(q)

        return snapshot.size > 0
          ? new Isbn(snapshot.docs[0].ref, snapshot.docs[0].data())
          : null
      },

      async getIsbnPrice () {
        const productsRef = collection(db, 'products')
        const productsQuery = query(productsRef, where('name', '==', 'ISBN'))
        const productsSnapshot = await getDocs(productsQuery)
        const productId = productsSnapshot.docs[0].id
        const pricesRef = collection(db, 'products', productId, 'prices')
        const pricesQuery = query(pricesRef, where('active', '==', true))
        const pricesSnapshot = await getDocs(pricesQuery)
        return {
          id: pricesSnapshot.docs[0].id,
          price: pricesSnapshot.docs[0].data().unit_amount
        }
      },

      async getUserInvoiceUrlForIsbn (isbnId) {
        const ref = collection(db, 'invoices')
        const q = query(ref,
          where('uid', '==', getAuth().currentUser.uid),
          where('metadata.isbnId', '==', isbnId),
          where('expire', '>=', new Date()),
          where('stripeInvoiceStatus', 'in', ['open', 'draft']),
          limit(1))
        const snapshot = await getDocs(q)

        return snapshot.size > 0
          ? snapshot.docs[0].data().stripeInvoiceUrl
          : null
      },

      async uploadIsbns (isbnList) {
        const batch = writeBatch(db)

        for (let i = 0; i < isbnList.length; i++) {
          const isbn = isbnList[i]
          const ref = doc(collection(db, 'isbn'))
          batch.set(ref, {
            value: isbn.trim(),
            status: 'available'
          })
        }

        await batch.commit()
      },

      async isAdmin () {
        const user = getAuth(app).currentUser
        if (!user) return false
        const token = await user.getIdTokenResult()
        return !!token.claims.admin
      },

      async uploadTemplate (template, options = { uploadImages: false, makeScreenshots: false }) {
        await new Promise((resolve) => setTimeout(resolve, 1000))

        const isAdmin = await this.isAdmin()

        console.log(template)
        console.log(options)

        if (options.uploadImages) {
          await uploadTemplateImages(template, options.onlyPageIds)
        }

        if (options.makeScreenshots) {
          await makePagesScreensots(template, options.onlyPageIds)
        }

        const batch = writeBatch(db)

        const templateData = template.toObject()
        if (!isAdmin) {
          delete templateData.available
        }

        batch.set(template._ref, templateData, { merge: true })

        for (let s = 0; s < template.sheets.length; s++) {
          const sheet = template.sheets[s]
          batch.set(sheet._ref, sheet.toObject())
        }

        for (let p = 0; p < template.pages.length; p++) {
          if (options.onlyPageIds?.length > 0 && !options.onlyPageIds.includes(template.pages[p]._id)) {
            continue
          }

          const page = template.pages[p]
          const pageData = JSON.parse(JSON.stringify(page.toObject()))
          if (!options.uploadImages) {
            delete pageData.images
          }
          if (!options.makeScreenshots) {
            delete pageData.previewUrl
          }
          console.log(pageData)
          batch.set(page._ref, pageData, { merge: true })
        }

        await batch.commit()
      },

      async getUsers (ref) {
        const snapshot = await getDocs(ref)
        return Promise.all(snapshot.docs.map((doc) => this.getUser(doc.ref)))
      },

      async getUser (ref) {
        const snapshot = await getDoc(ref)
        const data = snapshot.data()
        return new User(ref, data)
      },

      async getTemplatePrice (templateId) {
        const productsRef = collection(db, 'products')
        const productsQuery = query(productsRef, where('metadata.firebase_template_id', '==', templateId))
        const productsSnapshot = await getDocs(productsQuery)
        const productId = productsSnapshot.docs[0].id
        const pricesRef = collection(db, 'products', productId, 'prices')
        const pricesQuery = query(pricesRef, where('active', '==', true))
        const pricesSnapshot = await getDocs(pricesQuery)
        return {
          id: pricesSnapshot.docs[0].id,
          price: pricesSnapshot.docs[0].data().unit_amount
        }
      },

      async createCheckoutSession (data) {
        const checkoutSessionsRef = collection(db, 'customers', getAuth().currentUser.uid, 'checkout_sessions')
        const ref = await addDoc(checkoutSessionsRef, data)
        return new Promise((resolve) => {
          const unsub = onSnapshot(ref, (doc) => {
            if (doc.data().url) {
              unsub()
              resolve(doc.data().url)
            }
          })
        })
      },

      getFirebaseDocumentId () {
        return doc(collection(db, 'any')).id
      }
    }
  }
}

Vue.use(Firebase)

export default Firebase

async function uploadTemplateImages (template, onlyPageIds = null) {
  const storage = getStorage()
  const results = []

  const pagesToProcess = onlyPageIds
    ? template.pages.filter((page) => onlyPageIds.includes(page._id))
    : template.pages

  for (let p = 0; p < pagesToProcess.length; p++) {
    const page = pagesToProcess[p]

    if (typeof page.images === 'object') {
      for (const imageFileName in page.images) {
        if (/^blob:/.test(page.images[imageFileName])) { // upload only local images
          await new Promise((resolve) => {
            const imageFileRef = ref(storage, `${page._ref.path}/${imageFileName}`)

            getDownloadURL(imageFileRef).then((url) => {
              results.push({
                page: page,
                key: imageFileName,
                value: url
              })
              resolve()
            }).catch(() => {
              fetch(page.images[imageFileName])
                .then((result) => result.blob()).then((imageFile) => {
                  uploadBytes(imageFileRef, imageFile).then(() => {
                    getDownloadURL(imageFileRef).then((url) => {
                      results.push({
                        page: page,
                        key: imageFileName,
                        value: url
                      })
                      resolve()
                    })
                  })
                })
            })
          })
        }
      }
    }
  }

  results.forEach(({ page, key, value }) => {
    page.images[key] = value
  })
}

async function makePagesScreensots (template, onlyPageIds = null) {
  const storage = getStorage()

  const pagesToProcess = onlyPageIds
    ? template.pages.filter((page) => onlyPageIds.includes(page._id))
    : template.pages

  for (let p = 0; p < pagesToProcess.length; p++) {
    await new Promise((resolve) => {
      const page = pagesToProcess[p]
      const imageFileRef = ref(storage, `${page._ref.path}/preview`)
      domtoimage.toJpeg(document.querySelector(`#${page._id}`), { bgcolor: '#ffffff', quality: 0.85 }).then((imageUrl) =>
        fetch(imageUrl).then((result) => result.blob()).then((imageFile) =>
          uploadBytes(imageFileRef, imageFile).then(() =>
            getDownloadURL(imageFileRef).then((url) => {
              page.previewUrl = url
              resolve()
            }))))
    })
  }
}
